import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inventoryData: any) => void;
}
export function AddInventoryModal({
  isOpen,
  onClose,
  onSave
}: AddInventoryModalProps) {
  const {
    t
  } = useTranslation('shops');
  const [roomType, setRoomType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [availableRooms, setAvailableRooms] = useState('');
  const [closedToArrivalDates, setClosedToArrivalDates] = useState<string[]>([]);
  const [closedToDepartureDates, setClosedToDepartureDates] = useState<string[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [newClosedToArrivalDate, setNewClosedToArrivalDate] = useState('');
  const [newClosedToDepartureDate, setNewClosedToDepartureDate] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const handleAddClosedToArrivalDate = () => {
    if (newClosedToArrivalDate && !closedToArrivalDates.includes(newClosedToArrivalDate)) {
      setClosedToArrivalDates([...closedToArrivalDates, newClosedToArrivalDate]);
      setNewClosedToArrivalDate('');
    }
  };
  const handleAddClosedToDepartureDate = () => {
    if (newClosedToDepartureDate && !closedToDepartureDates.includes(newClosedToDepartureDate)) {
      setClosedToDepartureDates([...closedToDepartureDates, newClosedToDepartureDate]);
      setNewClosedToDepartureDate('');
    }
  };
  const handleAddBlackoutDate = () => {
    if (newBlackoutDate && !blackoutDates.includes(newBlackoutDate)) {
      setBlackoutDates([...blackoutDates, newBlackoutDate]);
      setNewBlackoutDate('');
    }
  };
  const handleRemoveClosedToArrivalDate = (date: string) => {
    setClosedToArrivalDates(closedToArrivalDates.filter(d => d !== date));
  };
  const handleRemoveClosedToDepartureDate = (date: string) => {
    setClosedToDepartureDates(closedToDepartureDates.filter(d => d !== date));
  };
  const handleRemoveBlackoutDate = (date: string) => {
    setBlackoutDates(blackoutDates.filter(d => d !== date));
  };
  const handleSubmit = () => {
    const inventoryData = {
      roomType,
      fromDate,
      toDate,
      availableRooms: parseInt(availableRooms, 10) || 0,
      closedToArrivalDates,
      closedToDepartureDates,
      blackoutDates
    };
    onSave(inventoryData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="add-inventory-title" data-id="element-10271">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-10272">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-10273">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-10274"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-10275">
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-10276">
            <h3 id="add-inventory-title" className="text-lg font-medium text-gray-900" data-id="element-10277">
              {t('components.AddInventoryModal.add_inventory')}
            </h3>
            <button onClick={onClose} aria-label={t('components.AddInventoryModal.add_inventory')} className="text-gray-400 hover:text-gray-500" data-id="element-10278">
              <XIcon size={20} aria-hidden="true" data-id="element-10279" />
            </button>
          </div>
          <div className="p-6" data-id="element-10280">
            <p className="text-sm text-gray-500 mb-4" data-id="element-10281">
              {t('components.AddInventoryModal.select_the_room_type_and_applicable')}
            </p>
            <div className="space-y-4" data-id="element-10282">
              {/* Room Type */}
              <div data-id="element-10283">
                <label htmlFor="ai-room-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10284">
                  {t('components.AddInventoryModal.room_type')}
                </label>
                <div className="relative" data-id="element-10285">
                  <select id="ai-room-type" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={roomType} onChange={e => setRoomType(e.target.value)} data-id="element-10286">
                    <option value="" data-id="element-10287">
                      {t('components.AddInventoryModal.select_room_type')}
                    </option>
                    <option value="premium-overwater" data-id="element-10288">
                      {t('components.AddInventoryModal.premium_overwater_villa')}
                    </option>
                    <option value="water-villa" data-id="element-10289">
                      {t('components.AddInventoryModal.water_villa')}
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10290">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10291" />
                  </div>
                </div>
              </div>
              {/* From Date */}
              <div data-id="element-10292">
                <label htmlFor="ai-from-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10293">
                  {t('components.AddInventoryModal.from')}
                </label>
                <input id="ai-from-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={fromDate} onChange={e => setFromDate(e.target.value)} data-id="element-10294" />
              </div>
              {/* To Date */}
              <div data-id="element-10295">
                <label htmlFor="ai-to-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10296">
                  {t('components.AddInventoryModal.to')}
                </label>
                <input id="ai-to-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={toDate} onChange={e => setToDate(e.target.value)} data-id="element-10297" />
              </div>
              {/* Available Rooms */}
              <div data-id="element-10298">
                <label htmlFor="ai-available-rooms" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10299">
                  {t('components.AddInventoryModal.daily_available_rooms')}
                </label>
                <input id="ai-available-rooms" type="number" placeholder={t('components.AddInventoryModal.attrs.enter_daily_available_rooms')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={availableRooms} onChange={e => setAvailableRooms(e.target.value)} data-id="element-10300" />
              </div>
              {/* Closed to Arrival dates */}
              <div data-id="element-10301">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10302">
                  {t('components.AddInventoryModal.closed_to_arrival_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-10303">
                  {closedToArrivalDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10304">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10305" />
                      <button onClick={() => handleRemoveClosedToArrivalDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10306">
                        <TrashIcon size={16} data-id="element-10307" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10308">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToArrivalDate} onChange={e => setNewClosedToArrivalDate(e.target.value)} data-id="element-10309" />
                    <button onClick={handleAddClosedToArrivalDate} className="ml-2 text-red-500" data-id="element-10310">
                      <TrashIcon size={16} data-id="element-10311" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToArrivalDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10312">
                    <PlusIcon size={16} className="mr-1" data-id="element-10313" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Closed to Departure dates */}
              <div data-id="element-10314">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10315">
                  {t('components.AddInventoryModal.closed_to_departure_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-10316">
                  {closedToDepartureDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10317">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10318" />
                      <button onClick={() => handleRemoveClosedToDepartureDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10319">
                        <TrashIcon size={16} data-id="element-10320" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10321">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToDepartureDate} onChange={e => setNewClosedToDepartureDate(e.target.value)} data-id="element-10322" />
                    <button onClick={handleRemoveClosedToDepartureDate} className="ml-2 text-red-500" data-id="element-10323">
                      <TrashIcon size={16} data-id="element-10324" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToDepartureDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10325">
                    <PlusIcon size={16} className="mr-1" data-id="element-10326" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Blackout dates */}
              <div data-id="element-10327">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10328">
                  {t('components.AddInventoryModal.blackout_dates_in_this_period')}
                </label>
                <div className="space-y-2" data-id="element-10329">
                  {blackoutDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-10330">
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-10331" />
                      <button onClick={() => handleRemoveBlackoutDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-10332">
                        <TrashIcon size={16} data-id="element-10333" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-10334">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)} data-id="element-10335" />
                    <button onClick={handleRemoveBlackoutDate} className="ml-2 text-red-500" data-id="element-10336">
                      <TrashIcon size={16} data-id="element-10337" />
                    </button>
                  </div>
                  <button onClick={handleAddBlackoutDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-10338">
                    <PlusIcon size={16} className="mr-1" data-id="element-10339" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end" data-id="element-10340">
            <button onClick={handleSubmit} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-10341">
              {t('components.AddInventoryModal.create')}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-10342">
      <polyline points="6 9 12 15 18 9" data-id="element-10343"></polyline>
    </svg>;
}